import { useState, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { workUpdateSchema } from '../validation/workUpdateSchema';
import { taskService } from '../services/taskService';

export const useWorkUpdate = ({ isOpen, onClose, task, onSuccess }) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(workUpdateSchema),
    mode: 'onChange',
    defaultValues: {
      updateStatus: '',
      title: '',
      description: '',
      progress: 0
    }
  });

  const statusVal = useWatch({ control, name: 'updateStatus' });
  const descriptionWatch = useWatch({ control, name: 'description' });
  const descLen = typeof descriptionWatch === 'string' ? descriptionWatch.length : 0;
  const progressVal = useWatch({ control, name: 'progress' }) ?? 0;
  
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Polinaidu: 10-05-2026 - Use reset when modal closes for consistency
  useEffect(() => {
    if (!isOpen) {
      reset({
        updateStatus: '',
        title: '',
        description: '',
        progress: 0
      });
      setFormError('');
    }
  }, [isOpen, reset]);

  // Polinaidu: 10-05-2026 - Initialize progress when modal opens
  useEffect(() => {
    if (isOpen && task) {
      setValue('progress', task.progress ?? 0);
    }
  }, [isOpen, task, setValue]);

  // Auto-update progress based on status/stages
  useEffect(() => {
    if (statusVal === 'COMPLETED') {
      setValue('progress', 100, { shouldValidate: true });
    } else if (statusVal === 'STARTED') {
      setValue('progress', 20, { shouldValidate: true });
    } else if (statusVal === 'IN_PROGRESS') {
      setValue('progress', 40, { shouldValidate: true });
    } else if (statusVal === 'ON_HOLD') {
      setValue('progress', 40, { shouldValidate: true });
    } else if (statusVal === 'WAITING_MATERIAL') {
      setValue('progress', 80, { shouldValidate: true });
    } else if (!statusVal) {
      setValue('progress', 0, { shouldValidate: true });
    }
  }, [statusVal, setValue]);

  const onSubmit = async (data) => {
    setSubmitting(true);
    setFormError('');
    try {
      const progress = data.updateStatus === 'COMPLETED' ? 100 : data.progress;
      await taskService.postWorkUpdate(task._id, {
        updateStatus: data.updateStatus,
        title: data.title.trim(),
        description: (data.description || '').trim(),
        progress
      });
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      const raw = err?.response?.data ?? err;
      const msg =
        typeof raw === 'string'
          ? raw
          : raw?.message || err?.message || 'Could not save update';
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return {
    register,
    handleSubmit,
    onSubmit,
    errors,
    watch,
    statusVal,
    descLen,
    progressVal,
    submitting,
    formError,
    setFormError,
  };
};
